import React, { useCallback, useMemo } from 'react';
import {
  func, bool, arrayOf, oneOf
} from 'prop-types';

import { surveyTypesAsNames } from 'unihow-common';
import { getLocalizedText } from '../../util';
import ExtraSurveyPartListRow from './ExtraSurveyPartListRow';
import SurveySchemaPartListRow from './SurveySchemaPartListRow';
import Pagination from '../Pagination';
import {
  surveySchemaMetadataType,
  surveySchemaPartType,
  surveyTypeValues
} from '../../constants/types';
import { withLang, langType } from '../../i18n';

import styles from './surveySchemaPartList.module.css';

const SurveySchemaPartList = ({
  schemaPartPage,
  onPageChange,
  isExtraPartsView,
  selectable,
  selectLimitOne,
  selectedParts,
  excludedParts,
  onSelectPart,
  type,
  translate: t,
  lang
}) => {
  const isSelectionDisabled = useMemo(
    () => (selectable && selectLimitOne && selectedParts.length >= 1),
    [selectable, selectLimitOne, selectedParts.length]
  );

  const getStatus = useCallback((schemaPart) => {
    const statusText = schemaPart.lockedFromEditing
      ? t('status.IN_USE') : t(`status.${schemaPart.status}`);
    const statusIconClassName = schemaPart.lockedFromEditing
      ? 'icon--permission-private' : 'icon--edit-event';

    return (
      <div className={styles.status}>
        <p>{statusText}
          <span className={`${styles.statusIcon} ${statusIconClassName}`} />
        </p>
      </div>
    );
  }, [t]);

  const headerRow = useMemo(() => (
    isExtraPartsView ? (
      <tr className={`${styles.header}`}>
        {selectable && <th />}
        <th>{t('common.name')}</th>
        <th>{t('surveyParts.identifier')}</th>
        <th>{t('common.description')}</th>
        <th className={styles.statusHeader}>{t('common.status')}</th>
        {selectable && <th />}
      </tr>
    ) : (
      <tr className={`${styles.header}`}>
        {selectable && <th />}
        <th>{t('surveyParts.identifier')}</th>
        <th>{t('common.name')}</th>
        <th>{t('common.description')}</th>
        <th className={styles.centeredText}>{t('common.version')}</th>
        <th className={styles.centeredText}>{t('common.status')}</th>
        {selectable && <th />}
      </tr>
    )
  ), [isExtraPartsView, selectable, t]);

  const selectedIds = useMemo(() => selectedParts.map((p) => p.id), [selectedParts]);
  const parts = useMemo(() => (
    (schemaPartPage.content || []).filter((part) =>
      !selectedIds.includes(part.id)
      && !excludedParts.some((e) => e.id === part.id))
  ), [schemaPartPage.content, selectedIds, excludedParts]);

  const rows = useMemo(() => (
    isExtraPartsView
      ? parts.map((schemaPart) => (
        <ExtraSurveyPartListRow
          key={schemaPart.id}
          surveyPartId={schemaPart.id}
          code={schemaPart.code}
          description={getLocalizedText(schemaPart.description, lang, t('surveyParts.descriptionMissing'))}
          translate={t}
          title={getLocalizedText(schemaPart.title, lang, t('surveyParts.titleMissing'))}
          onSelectSchemaPart={() => onSelectPart(schemaPart)}
          selectable={selectable}
          status={getStatus(schemaPart)}
          disabled={isSelectionDisabled}
        />
      ))
      : parts.map((schemaPart) => (
        <SurveySchemaPartListRow
          key={schemaPart.id}
          surveyPartId={schemaPart.id}
          code={schemaPart.code}
          description={getLocalizedText(schemaPart.description, lang, t('surveyParts.descriptionMissing'))}
          translate={t}
          version={schemaPart.version}
          title={getLocalizedText(schemaPart.title, lang, t('surveyParts.titleMissing'))}
          onSelectSchemaPart={() => onSelectPart(schemaPart)}
          selectable={selectable}
          status={getStatus(schemaPart)}
          disabled={isSelectionDisabled}
        />
      ))
  ), [isExtraPartsView, parts, lang, t, onSelectPart, selectable, getStatus, isSelectionDisabled]);

  const searchResultTitle = isExtraPartsView
    ? `${t('extraSurveyParts.searchResult')} (${schemaPartPage.totalElements})`
    : `${schemaPartPage.totalElements} ${surveyTypesAsNames[type] || ''}-${t('surveySchemaParts.schemaPartCount')}`;

  return (
    <div className={styles.container}>
      <h4>{searchResultTitle}</h4>
      <table className={styles.listing}>
        <thead>
          {headerRow}
        </thead>
        <tbody data-cy="list-body">
          {rows}
        </tbody>
      </table>

      {schemaPartPage.totalPages > 1 && (
        <Pagination
          navFunction={onPageChange}
          number={schemaPartPage.number}
          totalPages={schemaPartPage.totalPages}
          first={schemaPartPage.first}
          last={schemaPartPage.last}
          translate={t}
        />
      )}
    </div>
  );
};

SurveySchemaPartList.propTypes = {
  onPageChange: func.isRequired,
  isExtraPartsView: bool,
  selectable: bool,
  selectLimitOne: bool,
  selectedParts: arrayOf(surveySchemaMetadataType),
  excludedParts: arrayOf(surveySchemaPartType),
  onSelectPart: func,
  type: oneOf(Object.values(surveyTypeValues)).isRequired,
  ...langType
};

SurveySchemaPartList.defaultProps = {
  isExtraPartsView: false,
  selectable: false,
  selectLimitOne: false,
  selectedParts: [],
  excludedParts: [],
  onSelectPart: () => ({})
};

export default withLang(SurveySchemaPartList);
